/**
 * Method Registry — Canonical Source of Truth
 *
 * Encodes the full Methods.md matrix: every method, its variants, their session_fit
 * across S1–S6, output_forms, mechanism_tags, prerequisites, and the tool_id that
 * maps to the existing app implementation.
 *
 * This replaces the coarse producesRanking/producesTiers/producesScore flags in
 * TOOL_REGISTRY with a rich, S-class-aware model derived from the canonical document.
 *
 * AUTHORITATIVE SOURCE: Managing Priorities — Methods.md
 */

import type { DecisionClass } from '../types/prioritization/session';
import { isToolSwitchedOff } from './switchedOffTools';

// ============================================================================
// Core types
// ============================================================================

export type SessionFit = 'recommended' | 'allowed_warn' | 'not_fit';

export type OutputForm =
  | 'ranked_list'
  | 'tiered_list'
  | 'scored_list'
  | 'allocation_distribution'
  | 'two_by_two_map'
  | 'narrative_framing';

export type MechanismTag =
  | 'categorization'
  | 'direct_ranking'
  | 'pairwise_comparison'
  | 'weighted_scoring'
  | 'allocation'
  | 'threshold_sorting'
  | 'sequential_multi_pass'
  | 'tradeoff_lens';

export type MethodModality = 'Qualitative' | 'Quantitative' | 'Mixed';

export type BookCategory = 'Framework' | 'Simulation' | 'Sorting technique' | 'Hybrid method';

/**
 * Prerequisite codes — conditions that must be satisfied before a variant can run.
 */
export type MethodPrerequisiteCode =
  | 'METHOD_LOCKED'
  | 'PARTICIPATION_LOCKED'
  | 'POOL_LOCKED_FOR_S6'
  | 'CRITERIA_SET_LOCKED_FOR_S6'
  | 'VALUES_COMPLETE_FOR_CRITERIA'
  | 'LEVELING_PRESENT_WARN_ONLY'
  | 'POOL_HOMOGENEITY_WARN_ONLY';

export interface MethodVariant {
  variantId: string;
  modality: MethodModality;
  mechanismTags: MechanismTag[];
  outputForms: OutputForm[];
  sessionFit: Record<DecisionClass, SessionFit>;
  prerequisites: MethodPrerequisiteCode[];
  /** Links to the existing tool in TOOL_REGISTRY and app routing */
  toolId: string;
  /** Short notes from Methods.md */
  notes?: string;
  simulationLike?: boolean;
}

export interface MethodDefinition {
  methodId: string;
  name: string;
  bookCategory: BookCategory;
  aliases: string[];
  variants: MethodVariant[];
  /**
   * When set, the session launcher skips the modality question and pre-selects
   * this modality automatically. Use for tools where one modality is the
   * canonical experience and the other is an advanced/derived use case that
   * requires pre-existing workspace data (criteria + values) to be meaningful.
   */
  defaultModality?: 'Qualitative' | 'Quantitative';
}

// ============================================================================
// Helper to build session_fit records concisely
// ============================================================================

function fit(
  S1: SessionFit,
  S2: SessionFit,
  S3: SessionFit,
  S4: SessionFit,
  S5: SessionFit,
  S6: SessionFit,
): Record<DecisionClass, SessionFit> {
  return {
    S1_PARTICIPATION_PRIORITIZATION: S1,
    S2_ITEM_POOL_SELECTION: S2,
    S3_ATTRIBUTE_SELECTION: S3,
    S4_VALUES_CHOICE_SELECTION: S4,
    S5_VALUE_SELECTION: S5,
    S6_ITEM_PRIORITIZATION: S6,
  };
}

// Aliases for clarity
const R: SessionFit = 'recommended';
const W: SessionFit = 'allowed_warn';
const N: SessionFit = 'not_fit';

// ============================================================================
// The full registry — directly derived from Methods.md matrix
// ============================================================================

export const METHOD_REGISTRY: MethodDefinition[] = [
  // -----------------------------------------------------------------------
  // Eisenhower Matrix (Covey) — Framework
  // -----------------------------------------------------------------------
  {
    methodId: 'eisenhower_matrix',
    name: 'Eisenhower Matrix',
    bookCategory: 'Framework',
    aliases: ['Covey Matrix', 'Urgent-Important Matrix'],
    defaultModality: 'Qualitative',
    variants: [
      {
        variantId: 'eisenhower_qual',
        modality: 'Qualitative',
        mechanismTags: ['categorization'],
        outputForms: ['two_by_two_map', 'tiered_list'],
        sessionFit: fit(W, R, N, N, N, W),
        prerequisites: ['METHOD_LOCKED'],
        toolId: 'eisenhower-matrix',
        notes: 'Qualitative categorization into four quadrants by urgency and importance.',
      },
      {
        variantId: 'eisenhower_quant',
        modality: 'Quantitative',
        mechanismTags: ['weighted_scoring', 'categorization'],
        outputForms: ['scored_list', 'two_by_two_map'],
        sessionFit: fit(N, W, N, N, W, W),
        prerequisites: ['METHOD_LOCKED', 'VALUES_COMPLETE_FOR_CRITERIA'],
        toolId: 'eisenhower-matrix',
        notes: 'Numeric urgency/importance scores mapped onto quadrants.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Impact / Effort Matrix (Cost/Value) — Framework
  // -----------------------------------------------------------------------
  {
    methodId: 'impact_effort_matrix',
    name: 'Impact/Effort Matrix',
    bookCategory: 'Framework',
    aliases: ['Cost/Value Matrix', 'Value/Effort Matrix'],
    defaultModality: 'Qualitative',
    variants: [
      {
        variantId: 'impact_effort_qual',
        modality: 'Qualitative',
        mechanismTags: ['categorization'],
        outputForms: ['two_by_two_map', 'tiered_list'],
        sessionFit: fit(N, W, N, N, W, W),
        prerequisites: ['METHOD_LOCKED'],
        toolId: 'impact-effort-matrix',
        notes: 'Qualitative placement on impact/effort 2×2.',
      },
      {
        variantId: 'impact_effort_quant',
        modality: 'Quantitative',
        mechanismTags: ['weighted_scoring', 'categorization'],
        outputForms: ['scored_list', 'two_by_two_map', 'ranked_list'],
        sessionFit: fit(N, W, N, N, W, R),
        prerequisites: ['METHOD_LOCKED', 'VALUES_COMPLETE_FOR_CRITERIA', 'POOL_LOCKED_FOR_S6'],
        toolId: 'impact-effort-matrix',
        notes: 'Numeric scores on impact/effort axes; can produce a sorted ranking.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Max Priorities Pyramid — Framework
  // -----------------------------------------------------------------------
  {
    methodId: 'max_priorities_pyramid',
    name: 'Max Priorities Pyramid',
    bookCategory: 'Framework',
    aliases: [],
    defaultModality: 'Qualitative',
    variants: [
      {
        variantId: 'max_priorities_qual',
        modality: 'Qualitative',
        mechanismTags: ['threshold_sorting', 'categorization'],
        outputForms: ['tiered_list'],
        sessionFit: fit(N, R, N, N, N, W),
        prerequisites: ['METHOD_LOCKED'],
        toolId: 'max-priorities-pyramid',
        notes: 'Constrained focus hierarchy — select the top N items qualitatively.',
      },
      {
        variantId: 'max_priorities_quant',
        modality: 'Quantitative',
        mechanismTags: ['threshold_sorting', 'weighted_scoring'],
        outputForms: ['scored_list', 'tiered_list'],
        sessionFit: fit(N, W, N, N, W, W),
        prerequisites: ['METHOD_LOCKED', 'VALUES_COMPLETE_FOR_CRITERIA'],
        toolId: 'max-priorities-pyramid',
        notes: 'Numeric scores used to fill pyramid tiers.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Fast vs. Right Framework — Framework
  // -----------------------------------------------------------------------
  {
    methodId: 'fast_vs_right',
    name: 'Fast vs. Right Framework',
    bookCategory: 'Framework',
    aliases: ['Speed vs. Quality'],
    variants: [
      {
        variantId: 'fast_vs_right_qual',
        modality: 'Qualitative',
        mechanismTags: ['tradeoff_lens'],
        outputForms: ['narrative_framing', 'tiered_list'],
        sessionFit: fit(W, N, W, W, W, W),
        prerequisites: ['METHOD_LOCKED'],
        toolId: 'fast-vs-right',
        notes: 'Tradeoff lens for surfacing speed vs quality considerations across decisions.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Prune the Product Tree — Framework
  // -----------------------------------------------------------------------
  {
    methodId: 'prune_product_tree',
    name: 'Prune the Product Tree',
    bookCategory: 'Framework',
    aliases: ['Prune the Future'],
    variants: [
      {
        variantId: 'prune_tree_qual',
        modality: 'Qualitative',
        mechanismTags: ['categorization'],
        outputForms: ['tiered_list', 'narrative_framing'],
        sessionFit: fit(N, R, W, N, N, N),
        prerequisites: ['METHOD_LOCKED'],
        toolId: 'prune-product-tree',
        notes: 'Visual pruning of feature tree — primarily a pool selection tool.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Speedboat (Sailboat) — Framework
  // -----------------------------------------------------------------------
  {
    methodId: 'speedboat',
    name: 'Speed Boat',
    bookCategory: 'Framework',
    aliases: ['Sailboat', 'Speedboat Retrospective'],
    defaultModality: 'Qualitative',
    variants: [
      {
        variantId: 'speedboat_qual',
        modality: 'Qualitative',
        mechanismTags: ['categorization'],
        outputForms: ['tiered_list', 'narrative_framing'],
        sessionFit: fit(N, R, W, N, N, N),
        prerequisites: ['METHOD_LOCKED'],
        toolId: 'speed-boat',
        notes: 'Obstacle/force surfacing and clustering — a pool selection and framing tool.',
      },
      {
        variantId: 'speedboat_quant',
        modality: 'Quantitative',
        mechanismTags: ['allocation'],
        outputForms: ['allocation_distribution', 'ranked_list'],
        sessionFit: fit(N, W, N, N, N, W),
        prerequisites: ['METHOD_LOCKED', 'POOL_LOCKED_FOR_S6'],
        toolId: 'speed-boat',
        simulationLike: true,
        notes: 'Used as a constraint/allocation exercise.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // MoSCoW Method — Framework
  // -----------------------------------------------------------------------
  {
    methodId: 'moscow_method',
    name: 'MoSCoW Method',
    bookCategory: 'Framework',
    aliases: [],
    defaultModality: 'Qualitative',
    variants: [
      {
        variantId: 'moscow_qual',
        modality: 'Qualitative',
        mechanismTags: ['categorization', 'threshold_sorting'],
        outputForms: ['tiered_list'],
        sessionFit: fit(N, R, N, N, N, W),
        prerequisites: ['METHOD_LOCKED'],
        toolId: 'moscow-method',
        notes: 'Must / Should / Could / Won\'t categorical buckets — strong pool selection tool.',
      },
      {
        variantId: 'moscow_quant',
        modality: 'Quantitative',
        mechanismTags: ['threshold_sorting', 'weighted_scoring'],
        outputForms: ['tiered_list', 'scored_list'],
        sessionFit: fit(N, W, N, N, W, W),
        prerequisites: ['METHOD_LOCKED', 'VALUES_COMPLETE_FOR_CRITERIA'],
        toolId: 'moscow-method',
        notes: 'Numeric scoring fed into MoSCoW bucket thresholds.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Buy a Feature — Simulation
  // -----------------------------------------------------------------------
  {
    methodId: 'buy_a_feature',
    name: 'Buy A Feature',
    bookCategory: 'Simulation',
    aliases: [],
    variants: [
      {
        variantId: 'buy_a_feature_quant',
        modality: 'Quantitative',
        mechanismTags: ['allocation'],
        outputForms: ['allocation_distribution', 'ranked_list'],
        sessionFit: fit(N, W, N, N, N, W),
        prerequisites: ['METHOD_LOCKED', 'PARTICIPATION_LOCKED', 'POOL_LOCKED_FOR_S6'],
        toolId: 'buy-a-feature',
        simulationLike: true,
        notes: 'Budget-allocation simulation where participants spend virtual money on features.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Dot Voting — Simulation
  // -----------------------------------------------------------------------
  {
    methodId: 'dot_voting',
    name: 'Dot Voting',
    bookCategory: 'Simulation',
    aliases: ['Sticker Voting', 'Dot Democracy'],
    defaultModality: 'Qualitative',
    variants: [
      {
        variantId: 'dot_voting_qual',
        modality: 'Qualitative',
        mechanismTags: ['allocation'],
        outputForms: ['ranked_list', 'tiered_list'],
        sessionFit: fit(R, R, R, W, W, W),
        prerequisites: ['METHOD_LOCKED', 'PARTICIPATION_LOCKED'],
        toolId: 'dot-voting',
        notes: 'Multi-purpose: works for participation, pool selection, and criteria selection decisions.',
      },
      {
        variantId: 'dot_voting_quant',
        modality: 'Quantitative',
        mechanismTags: ['allocation', 'weighted_scoring'],
        outputForms: ['scored_list', 'ranked_list'],
        sessionFit: fit(N, W, N, N, W, W),
        prerequisites: ['METHOD_LOCKED', 'PARTICIPATION_LOCKED', 'VALUES_COMPLETE_FOR_CRITERIA'],
        toolId: 'dot-voting',
        notes: 'Dot counts treated as numeric scores for quantitative ranking.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Stack Ranking (Force Ranking) — Sorting technique
  // -----------------------------------------------------------------------
  {
    methodId: 'stack_ranking',
    name: 'Stack Ranking',
    bookCategory: 'Sorting technique',
    aliases: ['Force Ranking'],
    variants: [
      {
        variantId: 'stack_ranking_quant',
        modality: 'Quantitative',
        mechanismTags: ['direct_ranking'],
        outputForms: ['ranked_list'],
        sessionFit: fit(N, W, N, N, N, R),
        prerequisites: ['METHOD_LOCKED', 'POOL_LOCKED_FOR_S6', 'PARTICIPATION_LOCKED'],
        toolId: 'stack-ranking',
        notes: 'Pure forced linear ordering. Primary tool for item prioritization.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Pairwise Comparison — Sorting technique
  // -----------------------------------------------------------------------
  {
    methodId: 'pairwise_comparison',
    name: 'Pairwise Comparison',
    bookCategory: 'Sorting technique',
    aliases: ['Paired Comparison'],
    variants: [
      {
        variantId: 'pairwise_quant',
        modality: 'Quantitative',
        mechanismTags: ['pairwise_comparison'],
        outputForms: ['ranked_list', 'scored_list'],
        sessionFit: fit(N, W, N, N, N, R),
        prerequisites: ['METHOD_LOCKED', 'POOL_LOCKED_FOR_S6', 'PARTICIPATION_LOCKED'],
        toolId: 'pairwise-ranking',
        notes: 'Compare items two at a time to derive a ranked order. Best with ≤20 items.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // RICE Scoring — Sorting technique
  // -----------------------------------------------------------------------
  {
    methodId: 'rice_scoring',
    name: 'RICE Scoring',
    bookCategory: 'Sorting technique',
    aliases: [],
    variants: [
      {
        variantId: 'rice_quant',
        modality: 'Quantitative',
        mechanismTags: ['weighted_scoring'],
        outputForms: ['scored_list', 'ranked_list'],
        sessionFit: fit(N, W, N, N, W, R),
        prerequisites: ['METHOD_LOCKED', 'POOL_LOCKED_FOR_S6', 'CRITERIA_SET_LOCKED_FOR_S6', 'VALUES_COMPLETE_FOR_CRITERIA'],
        toolId: 'rice-scoring',
        notes: 'Reach × Impact × Confidence ÷ Effort formula scoring.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Simple Weighted Scoring — Sorting technique
  // -----------------------------------------------------------------------
  {
    methodId: 'simple_weighted_scoring',
    name: 'Weighted Scoring',
    bookCategory: 'Sorting technique',
    aliases: ['Simple Weighted Scoring'],
    variants: [
      {
        variantId: 'weighted_scoring_quant',
        modality: 'Quantitative',
        mechanismTags: ['weighted_scoring'],
        outputForms: ['scored_list', 'ranked_list'],
        sessionFit: fit(N, W, N, W, W, R),
        prerequisites: ['METHOD_LOCKED', 'POOL_LOCKED_FOR_S6', 'CRITERIA_SET_LOCKED_FOR_S6', 'VALUES_COMPLETE_FOR_CRITERIA'],
        toolId: 'simple-weighted-scoring',
        notes: 'Custom criteria with configurable weights.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // WSJF — Sorting technique
  // -----------------------------------------------------------------------
  {
    methodId: 'wsjf',
    name: 'WSJF',
    bookCategory: 'Sorting technique',
    aliases: ['Weighted Shortest Job First'],
    variants: [
      {
        variantId: 'wsjf_quant',
        modality: 'Quantitative',
        mechanismTags: ['weighted_scoring'],
        outputForms: ['scored_list', 'ranked_list'],
        sessionFit: fit(N, W, N, N, W, R),
        prerequisites: ['METHOD_LOCKED', 'POOL_LOCKED_FOR_S6', 'CRITERIA_SET_LOCKED_FOR_S6', 'VALUES_COMPLETE_FOR_CRITERIA'],
        toolId: 'wsjf',
        notes: 'SAFe formula: (Business Value + Time Criticality + Risk Reduction) ÷ Job Size.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Pareto Analysis (80/20) — Sorting technique
  // -----------------------------------------------------------------------
  {
    methodId: 'pareto_analysis',
    name: 'Pareto Analysis',
    bookCategory: 'Sorting technique',
    aliases: ['80/20 Rule', 'Pareto Principle'],
    variants: [
      {
        variantId: 'pareto_qual',
        modality: 'Qualitative',
        mechanismTags: ['threshold_sorting'],
        outputForms: ['tiered_list'],
        sessionFit: fit(N, W, N, N, W, W),
        prerequisites: ['METHOD_LOCKED'],
        toolId: 'pareto-analysis',
        notes: 'Qualitative identification of the vital few vs. trivial many.',
      },
      {
        variantId: 'pareto_quant',
        modality: 'Quantitative',
        mechanismTags: ['threshold_sorting', 'weighted_scoring'],
        outputForms: ['tiered_list', 'scored_list'],
        sessionFit: fit(N, W, N, N, N, W),
        prerequisites: ['METHOD_LOCKED', 'VALUES_COMPLETE_FOR_CRITERIA'],
        toolId: 'pareto-analysis',
        notes: 'Cumulative impact percentage used to identify the 80% contributors.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Cost of Delay — Framework (Qual) / Sorting technique (Quant)
  // -----------------------------------------------------------------------
  {
    methodId: 'cost_of_delay',
    name: 'Cost of Delay',
    bookCategory: 'Framework',
    aliases: ['CoD'],
    variants: [
      {
        variantId: 'cost_of_delay_qual',
        modality: 'Qualitative',
        mechanismTags: ['tradeoff_lens'],
        outputForms: ['narrative_framing'],
        sessionFit: fit(N, W, N, W, W, W),
        prerequisites: ['METHOD_LOCKED'],
        toolId: 'cost-of-delay',
        notes: 'Qualitative framing of time value.',
      },
      {
        variantId: 'cost_of_delay_quant',
        modality: 'Quantitative',
        mechanismTags: ['threshold_sorting', 'weighted_scoring'],
        outputForms: ['scored_list', 'ranked_list'],
        sessionFit: fit(N, W, N, N, W, R),
        prerequisites: ['METHOD_LOCKED', 'POOL_LOCKED_FOR_S6', 'CRITERIA_SET_LOCKED_FOR_S6', 'VALUES_COMPLETE_FOR_CRITERIA'],
        toolId: 'cost-of-delay',
        notes: 'Numeric CoD signal used to sort. ✅/⚠️ in the matrix.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Analytic Hierarchy Process (AHP) — Hybrid method
  // -----------------------------------------------------------------------
  {
    methodId: 'ahp',
    name: 'AHP',
    bookCategory: 'Hybrid method',
    aliases: ['Analytic Hierarchy Process'],
    variants: [
      {
        variantId: 'ahp_quant',
        modality: 'Quantitative',
        mechanismTags: ['pairwise_comparison', 'weighted_scoring'],
        outputForms: ['scored_list', 'ranked_list'],
        sessionFit: fit(N, W, N, N, N, R),
        prerequisites: ['METHOD_LOCKED', 'POOL_LOCKED_FOR_S6', 'CRITERIA_SET_LOCKED_FOR_S6', 'VALUES_COMPLETE_FOR_CRITERIA', 'PARTICIPATION_LOCKED'],
        toolId: 'ahp',
        notes: 'Full AHP: pairwise criterion weighting + pairwise item comparison. Best with ≤15 items.',
      },
    ],
  },

  // -----------------------------------------------------------------------
  // Magic Prioritization Trick — Sorting technique
  // -----------------------------------------------------------------------
  {
    methodId: 'magic_prioritization',
    name: 'Magic Prioritization',
    bookCategory: 'Sorting technique',
    aliases: ['Magic Prioritization Trick'],
    variants: [
      {
        variantId: 'magic_qual',
        modality: 'Qualitative',
        mechanismTags: ['sequential_multi_pass'],
        outputForms: ['tiered_list', 'narrative_framing'],
        sessionFit: fit(N, W, N, N, W, W),
        prerequisites: ['METHOD_LOCKED'],
        toolId: 'magic-prioritization',
        notes: 'Staged decoupling to surface CD3 qualitatively.',
      },
      {
        variantId: 'magic_quant',
        modality: 'Quantitative',
        mechanismTags: ['sequential_multi_pass', 'weighted_scoring'],
        outputForms: ['scored_list', 'ranked_list'],
        sessionFit: fit(N, W, N, N, W, R),
        prerequisites: ['METHOD_LOCKED', 'POOL_LOCKED_FOR_S6', 'VALUES_COMPLETE_FOR_CRITERIA'],
        toolId: 'magic-prioritization',
        notes: 'Numeric CD3 signal used to produce a ranked list.',
      },
    ],
  },
];

// ============================================================================
// Accessor functions
// ============================================================================

/** All variants for a given tool slug */
export function getVariantsForTool(toolId: string): MethodVariant[] {
  return METHOD_REGISTRY.flatMap((m) =>
    m.variants.filter((v) => v.toolId === toolId)
  );
}

/** The method definition that owns a given variantId */
export function getMethodForVariant(variantId: string): MethodDefinition | undefined {
  return METHOD_REGISTRY.find((m) => m.variants.some((v) => v.variantId === variantId));
}

/** A specific variant by its ID */
export function getVariantById(variantId: string): MethodVariant | undefined {
  return METHOD_REGISTRY.flatMap((m) => m.variants).find((v) => v.variantId === variantId);
}

/** Session fit for a specific variant and decision class */
export function getFitForVariant(variantId: string, sClass: DecisionClass): SessionFit {
  const variant = getVariantById(variantId);
  return variant?.sessionFit[sClass] ?? 'not_fit';
}

/** Output forms a given variant produces */
export function getOutputForms(variantId: string): OutputForm[] {
  return getVariantById(variantId)?.outputForms ?? [];
}

/** Prerequisites for a given variant */
export function getPrerequisites(variantId: string): MethodPrerequisiteCode[] {
  return getVariantById(variantId)?.prerequisites ?? [];
}

/** All variants recommended or allowed for an S-class, sorted: recommended first */
export function getVariantsForSClass(
  sClass: DecisionClass,
  excludeNotFit = true,
): MethodVariant[] {
  const all = METHOD_REGISTRY.flatMap((m) => m.variants);
  return all
    .filter((v) => !isToolSwitchedOff(v.toolId))
    .filter((v) => !excludeNotFit || v.sessionFit[sClass] !== 'not_fit')
    .sort((a, b) => {
      const order: Record<SessionFit, number> = { recommended: 0, allowed_warn: 1, not_fit: 2 };
      return order[a.sessionFit[sClass]] - order[b.sessionFit[sClass]];
    });
}

/** All tool IDs that are recommended for a given S-class */
export function getRecommendedToolsForSClass(sClass: DecisionClass): string[] {
  return [
    ...new Set(
      getVariantsForSClass(sClass)
        .filter((v) => v.sessionFit[sClass] === 'recommended')
        .map((v) => v.toolId)
    ),
  ];
}

/**
 * Returns the default modality for a tool, if one is declared.
 * The session launcher uses this to skip the modality question for tools
 * where one variant is the canonical experience.
 */
export function getDefaultModalityForTool(
  toolId: string,
): 'Qualitative' | 'Quantitative' | undefined {
  const method = METHOD_REGISTRY.find((m) => m.variants.some((v) => v.toolId === toolId));
  return method?.defaultModality;
}

/** Best variants for a tool at a given S-class (recommended > allowed_warn > not_fit) */
export function getBestVariantsForToolAndSClass(
  toolId: string,
  sClass: DecisionClass,
): MethodVariant[] {
  return getVariantsForTool(toolId).filter((v) => v.sessionFit[sClass] !== 'not_fit');
}

/**
 * Returns all mechanism tags for a given toolId (across all variants).
 * The first element is the primary mechanism tag for the tool's canonical variant.
 * Returns an empty array if the toolId is not found.
 *
 * Used by stageToolValidator to gate session creation (Canon 23 §7.2, Gap 3).
 */
export function getMechanismTagsForToolId(toolId: string): MechanismTag[] {
  const variants = getVariantsForTool(toolId);
  if (variants.length === 0) return [];
  // Flatten and de-duplicate mechanism tags; primary variant's tags come first.
  const seen = new Set<MechanismTag>();
  const result: MechanismTag[] = [];
  for (const variant of variants) {
    for (const tag of variant.mechanismTags) {
      if (!seen.has(tag)) {
        seen.add(tag);
        result.push(tag);
      }
    }
  }
  return result;
}
